import React, { useState, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, Image } from 'react-native';
import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import SupermedicLogoFacilities from '../../../../assets/images/supermedic-logo-facilities-web.png';
import { webColors, webBorderRadius, webShadows } from '../../../../constants/webStyles';
import { login } from '../../../../services/auth';
import { setLoggedIn } from '../../../../reducers/auth/user';
import api from '../../../../services/api';

const LoginView = ({ onShowForgotPassword, onShowRegister, navigation }) => {
  const dispatch = useDispatch();
  const passwordInputRef = useRef(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const loginMutation = useMutation(
    (data) => login(data),
    {
      onError: (err) => {
        setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
      },
      onSuccess: (res) => {
        const { token, slug, country, scope } = res.data.data;
        api.defaults.headers.common.Authorization = `Bearer ${token}`;
        dispatch(setLoggedIn({ token, slug, country, isDoctor: scope === 'doctor' }));
        navigation.reset({
          index: 0,
          routes: [{ name: 'HomeScreen' }],
        });
      },
    }
  );

  const handleLogin = () => {
    setError('');

    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    loginMutation.mutate({
      email,
      password,
      scope: 'clinic_owner',
    });
  };

  const isLoading = loginMutation.isLoading;

  return (
    <View style={styles.container}>
      <View style={styles.formLogo}>
        <Image
          source={SupermedicLogoFacilities}
          style={styles.logoImage}
          resizeMode="contain"
        />
      </View>

      {error ? (
        <View style={styles.errorBox}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      ) : null}

      <View style={styles.form}>
        <View style={styles.inputWrapper}>
          <View style={styles.inputIcon}>
            <Mail size={18} color={webColors.textMuted} />
          </View>
          <TextInput
            style={styles.input}
            placeholder="Email"
            placeholderTextColor={webColors.textMuted}
            value={email}
            onChangeText={setEmail}
            keyboardType="email-address"
            autoCapitalize="none"
            editable={!isLoading}
            returnKeyType="next"
            onSubmitEditing={() => passwordInputRef.current?.focus()}
            blurOnSubmit={false}
          />
        </View>

        <View style={styles.inputWrapper}>
          <View style={styles.inputIcon}>
            <Lock size={18} color={webColors.textMuted} />
          </View>
          <TextInput
            ref={passwordInputRef}
            style={[styles.input, styles.inputWithToggle]}
            placeholder="Password"
            placeholderTextColor={webColors.textMuted}
            value={password}
            onChangeText={setPassword}
            secureTextEntry={!showPassword}
            editable={!isLoading}
            returnKeyType="go"
            onSubmitEditing={handleLogin}
          />
          <TouchableOpacity
            style={styles.togglePassword}
            onPress={() => setShowPassword(!showPassword)}
          >
            {showPassword ? (
              <EyeOff size={18} color={webColors.textMuted} />
            ) : (
              <Eye size={18} color={webColors.textMuted} />
            )}
          </TouchableOpacity>
        </View>

        <TouchableOpacity
          style={styles.forgotLink}
          onPress={onShowForgotPassword}
        >
          <Text style={styles.forgotLinkText}>Forgot Password</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.btnLogin, isLoading && styles.btnLoginDisabled]}
          onPress={handleLogin}
          disabled={isLoading}
        >
          <Text style={styles.btnLoginText}>
            {isLoading ? 'Signing in...' : 'Sign in'}
          </Text>
        </TouchableOpacity>

        <View style={styles.createAccountLink}>
          <Text style={styles.createAccountText}>Don't have an account?</Text>
          <TouchableOpacity onPress={onShowRegister}>
            <Text style={styles.createAccountLinkText}>Create an account</Text>
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.footer}>
        <Text style={styles.supportEmail}>support@supermedic.health</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 20,
  },
  formLogo: {
    alignItems: 'center',
    marginBottom: 48,
  },
  logoImage: {
    width: '100%',
    maxWidth: 280,
    height: 90,
    alignSelf: 'center',
  },
  errorBox: {
    backgroundColor: webColors.errorLight,
    padding: 12,
    borderRadius: webBorderRadius.small,
    marginBottom: 16,
  },
  errorText: {
    color: webColors.error,
    fontSize: 14,
  },
  form: {
    gap: 16,
  },
  inputWrapper: {
    position: 'relative',
  },
  inputIcon: {
    position: 'absolute',
    left: 16,
    top: '50%',
    transform: [{ translateY: -9 }],
    zIndex: 1,
  },
  input: {
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    paddingVertical: 16,
    paddingLeft: 48,
    paddingRight: 16,
    fontSize: 15,
    color: webColors.textPrimary,
  },
  inputWithToggle: {
    paddingRight: 48,
  },
  togglePassword: {
    position: 'absolute',
    right: 16,
    top: '50%',
    transform: [{ translateY: -9 }],
  },
  forgotLink: {
    alignSelf: 'flex-end',
  },
  forgotLinkText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.primary,
  },
  btnLogin: {
    backgroundColor: webColors.primary,
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
    marginTop: 8,
    ...webShadows.button,
  },
  btnLoginDisabled: {
    opacity: 0.7,
  },
  btnLoginText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  createAccountLink: {
    alignItems: 'center',
    marginTop: 16,
  },
  createAccountText: {
    fontSize: 15,
    color: '#6b7280',
    marginBottom: 4,
  },
  createAccountLinkText: {
    fontSize: 16,
    fontWeight: '600',
    color: webColors.primary,
  },
  footer: {
    alignItems: 'center',
    paddingTop: 24,
    marginTop: 32,
    borderTopWidth: 1,
    borderTopColor: webColors.border,
  },
  supportEmail: {
    fontSize: 14,
    color: webColors.textMuted,
  },
});

export default LoginView;
